import {
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Paper,
    CircularProgress,
    Toolbar,
    Fade,
    LinearProgress,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import PreviewIcon from '@mui/icons-material/Preview'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useCallback, useContext, useReducer, useRef } from 'react'
import { ToastMessage } from '../../../context/ToastPopUpContext'
import { FormSection, SaveButton, Grid } from '../../UniversalComponents'
import { excelFileParser } from '../../../utilities/functionTools'
import responseError from '../../../context/responseError/responseError'
import { addMultipleDevices } from '../../../apiRoutes'
import { useAuth } from '../../../context/AuthenticationContext'
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material'
import {
    GlobalState,
    useGlobalState,
} from '../../../context/GlobalStateContext'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import { CloudUploadIcon, UploadIcon } from 'lucide-react'

type multipleDeviceUpload = {
    manufacturer: string
    deviceSerial: string
    provider: string
    partNumber: string
    shortHin: string
    iccid: string
    fullHin: string
}

const MultipleDeviceUploader = ({ closeModal, onClose }) => {
    const defaultState = {
        selectedFile: null,
        open: false,
        preview: [],
        deviceData: [],
        isFormDirty: false,
        uploading: false,
        progress: 0,
        status: '',
        error: null,
    }

    const reducer = (state: any, newState: any) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const setToastMessage = useContext(ToastMessage)
    const {
        selectedFile,
        open,
        preview,
        isFormDirty,
        deviceData,
        status,
        uploading,
        progress,
        error,
    } = state
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const MAX_SINGLE_REQUEST = 400
    const CHUNK_SIZE = 400
    const { activeManufacturer } = oupUser

    const MAX_FILE_SIZE = 10 * 1024 * 1024
    const ALLOWED_EXTENSIONS = ['csv', 'xlsx', 'xls', 'json']

    const validateDTC = (device: multipleDeviceUpload): boolean => {
        return !!device.deviceSerial && !!device.shortHin
    }

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const uploadedFile = e.target.files?.[0]
            if (!uploadedFile) return

            if (uploadedFile.size > MAX_FILE_SIZE) {
                setState({
                    error: 'File size exceeds 10MB limit',
                })

                return
            }

            const ext = uploadedFile.name.split('.').pop()?.toLowerCase()
            if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
                setState({
                    error: 'Invalid file type. Please use CSV, XLSX, XLS, or JSON',
                })
                return
            }

            setState({ selectedFile: uploadedFile })

            try {
                let jsonData: any[] = []

                if (ext === 'csv') {
                    const text = await uploadedFile.text()
                    const parsed = Papa.parse(text, {
                        header: true,
                        skipEmptyLines: true,
                        transform: (value) => value.trim(),
                    })
                    if (parsed.errors.length > 0) {
                        throw new Error('CSV parsing errors detected')
                    }
                    jsonData = parsed.data
                } else if (ext === 'xlsx' || ext === 'xls') {
                    const data = await uploadedFile.arrayBuffer()
                    const workbook = XLSX.read(data)
                    const worksheet = workbook.Sheets[workbook.SheetNames[0]]
                    jsonData = XLSX.utils.sheet_to_json(worksheet)
                } else if (ext === 'json') {
                    const text = await uploadedFile.text()
                    jsonData = JSON.parse(text)
                }

                const normalized: multipleDeviceUpload[] = jsonData.map(
                    (item) => ({
                        deviceSerial: item.deviceSerial,
                        shortHin: item?.shortHin,
                        iccid: item?.iccid || '',
                        fullHin: item?.fullHin || '',
                        provider: 'T-Mobile',
                        partNumber: '65df8102854790df54121fe1',
                        manufacturer: activeManufacturer?._id,
                    })
                )

                const valid = normalized?.filter((d) => validateDTC(d))

                if (valid.length === 0) {
                    throw new Error('No valid device records found in the file')
                }

                setState({
                    preview: valid,
                    deviceData: valid,
                    isFormDirty: true,
                })
                setToastMessage(
                    `File: ${uploadedFile?.name} uploaded successfully!`,
                    'success'
                )
            } catch (err: any) {
                console.error('File processing error:', err)

                setState({
                    error:
                        err.message ||
                        'Failed to parse file. Please check the format.',
                })

                setState({
                    preview: [],
                    deviceData: [],
                    selectedFile: null,
                })
            }
        },
        []
    )

    const handleReset = () => {
        setState({
            selectedFile: null,
            preview: [],
            deviceData: [],
            isFormDirty: false,
        })
        setToastMessage(`Form has been reset`, 'info')
    }

    // const handleSubmit = async () => {
    // old handlesubmit before chunking saved for historical refference
    //     try {
    //         const stringifyDeviceData = JSON.stringify(deviceData)
    //         const manufacturer = activeManufacturer?._id

    //         const data = { stringifyDeviceData, manufacturer }

    //         let res: any

    //         {
    //             res = await addMultipleDevices({ data })
    //             if (res) {
    //                 const { data, status, counts } = res

    //                 if (data && status === 200) {
    //                     const { counts } = data
    //                     const { boats, devices } = counts
    //                     setToastMessage(
    //                         `${boats}-Boats & ${devices}-Devices were created successfully`,
    //                         'success'
    //                     )
    //                     closeModal()
    //                     onClose()
    //                 } else {
    //                     responseError(res, data)
    //                 }
    //             }
    //         }
    //     } catch (err: any) {
    //         console.error(err, 'error')
    //         setToastMessage(`${err}`, 'error')
    //     }
    // }

    const handleSubmit = async () => {
        if (!deviceData || deviceData.length === 0) return

        setState({
            uploading: true,
            progress: 0,
            status: 'Starting upload...',
        })

        const manufacturer = activeManufacturer?._id
        const totalRows = deviceData?.length

        const sendChunk = async (chunk: any[]) => {
            const res = await addMultipleDevices({
                data: {
                    stringifyDeviceData: JSON.stringify(chunk),
                    manufacturer,
                },
            })

            if (!res || res.status !== 200) {
                const errorMsg = res?.data?.error || 'Upload failed'
                throw new Error(errorMsg)
            }

            return res.data
        }

        try {
            if (totalRows <= MAX_SINGLE_REQUEST) {
                setState({ status: `Uploading ${totalRows} rows...` })

                const result = await sendChunk(deviceData)

                setState({ progress: 100 })
                setToastMessage(
                    `${result.counts.boats} Boats & ${result.counts.devices} Devices created!`,
                    'success'
                )
                closeModal()
                onClose()
            } else {
                let uploaded = 0
                const totalChunks = Math.ceil(totalRows / CHUNK_SIZE)

                setState({
                    status: `Large file detected — uploading in ${totalChunks} chunks...`,
                })

                for (let i = 0; i < totalRows; i += CHUNK_SIZE) {
                    const chunk = deviceData.slice(i, i + CHUNK_SIZE)
                    const chunkNum = Math.floor(i / CHUNK_SIZE) + 1

                    try {
                        const result = await sendChunk(chunk)

                        uploaded += chunk.length
                        const percent = Math.round((uploaded / totalRows) * 100)

                        setState({
                            progress: percent,
                            status: `Chunk ${chunkNum}/${totalChunks} complete — ${uploaded.toLocaleString()} of ${totalRows.toLocaleString()} rows`,
                        })

                        setToastMessage(
                            `Chunk ${chunkNum}/${totalChunks}: ${result.counts.boats} boats + ${result.counts.devices} devices`,
                            'success'
                        )
                    } catch (err: any) {
                        console.error(`Chunk ${chunkNum} failed:`, err)
                        setToastMessage(
                            `Chunk ${chunkNum} failed: ${err.message}`,
                            'error'
                        )
                    }

                    await new Promise((r) => setTimeout(r, 200))
                }

                setState({ progress: 100 })
                setToastMessage(
                    `Mass import complete! ${totalRows.toLocaleString()} total rows processed.`,
                    'success'
                )
                closeModal()
                onClose()
            }
        } catch (err: any) {
            console.error('Import failed:', err)
            setToastMessage(`Import failed: ${err.message}`, 'error')
            setState({ uploading: false })
        }
    }

    const openGuidelines = () => setState({ open: true })

    return (
        <Grid container spacing={2} sx={{ marginTop: 3 }}>
            <FormSection
                reset={
                    <Button onClick={openGuidelines}>
                        <HelpOutlineIcon />
                    </Button>
                }
            >
                <Grid item xs={12}>
                    <Grid item xs={12} md={12}>
                        <Toolbar
                            sx={{
                                bgcolor: 'background.paper',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                borderRadius: '8px',
                                py: 1.5,
                                px: 3,
                                width: '100%',
                            }}
                        >
                            <Grid
                                container
                                alignItems="center"
                                spacing={1.5}
                                sx={{ justifyContent: 'space-between' }}
                            >
                                <Grid item xs={12} md={12} lg={12}>
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            component="div"
                                            sx={{
                                                fontWeight: 600,
                                                color: 'primary.main',
                                                display: {
                                                    xs: 'none',
                                                    sm: 'block',
                                                },
                                            }}
                                        >
                                            Device File Uploader
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* File Info and Progress */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        {selectedFile && (
                                            <Fade in={!!selectedFile}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mb: 0,
                                                    }}
                                                >
                                                    Selected:{' '}
                                                    {selectedFile.name} (
                                                    {(
                                                        selectedFile.size /
                                                        1024 /
                                                        1024
                                                    ).toFixed(2)}{' '}
                                                    MB)
                                                </Typography>
                                            </Fade>
                                        )}
                                    </Box>
                                </Grid>

                                {/* Action Buttons */}
                                <Grid
                                    item
                                    xs={12}
                                    md={8}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: 1,
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<UploadIcon />}
                                        sx={{
                                            borderRadius: '20px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            px: 2.5,
                                            py: 0.75,
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                borderColor: 'primary.main',
                                            },
                                        }}
                                    >
                                        Select File
                                        <input
                                            type="file"
                                            accept=".csv,.xlsx,.xls,.json"
                                            hidden
                                            onChange={handleFileChange}
                                        />
                                    </Button>

                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<RestartAltIcon />}
                                        disabled={
                                            !selectedFile ||
                                            preview.length === 0
                                        }
                                        onClick={handleReset}
                                        sx={{
                                            borderRadius: '20px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            px: 2.5,
                                            py: 0.75,
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                bgcolor: 'error.dark',
                                                color: 'black',
                                                borderColor: 'black',
                                                boxShadow:
                                                    '0 2px 8px rgba(0, 0, 0, 0.2)',
                                            },
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                        </Toolbar>

                        <Dialog
                            open={open}
                            onClose={() => setState({ open: false })}
                            maxWidth="sm"
                            fullWidth
                        >
                            <DialogTitle>
                                Instructions for Excel File
                            </DialogTitle>
                            <DialogContent>
                                <Typography variant="body1" gutterBottom>
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>Required Fields/Columns:</li>
                                    </ul>
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{ marginLeft: 2 }}
                                >
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>deviceSerial</li>
                                    </ul>

                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>shortHin</li>
                                    </ul>
                                </Typography>

                                <Typography
                                    variant="body1"
                                    gutterBottom
                                    sx={{ marginTop: 2 }}
                                >
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>Optional Fields/Columns:</li>
                                    </ul>
                                </Typography>

                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{ marginLeft: 2 }}
                                >
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>iccid</li>
                                    </ul>
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>fullHin</li>
                                    </ul>

                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '3.5rem',
                                        }}
                                    >
                                        <li>
                                            iccid or fullHIn can be filled in or
                                            left blank.
                                        </li>
                                    </ul>
                                </Typography>

                                <Typography
                                    variant="body1"
                                    gutterBottom
                                    sx={{ marginTop: 2 }}
                                >
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>
                                            Fields/Columns are case sensitive
                                            incorrect input will result in
                                            errors.
                                        </li>
                                    </ul>
                                    <br />

                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>
                                            {' '}
                                            Field/Column order is not required.
                                        </li>
                                    </ul>
                                    <br />
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li> Maximum File size is 10MB.</li>
                                    </ul>
                                </Typography>
                            </DialogContent>
                        </Dialog>
                    </Grid>
                </Grid>

                <Grid item xs={12} sx={{ padding: 2 }}>
                    {preview?.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1">
                                Preview ({preview?.length} valid records)
                            </Typography>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1,
                                    maxHeight: 500,
                                    overflow: 'auto',
                                    bgcolor: 'background.default',
                                }}
                            >
                                <pre
                                    style={{
                                        fontSize: 12,
                                        margin: 0,
                                    }}
                                >
                                    <Box sx={{ minWidth: 600 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                bgcolor: 'background.paper',
                                                p: 1,
                                                fontWeight: 'bold',
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    flex: 0.125,
                                                    px: 1,
                                                }}
                                            >
                                                Device Serial
                                            </Box>
                                            <Box
                                                sx={{
                                                    flex: 0.125,
                                                    px: 1,
                                                }}
                                            >
                                                HIN
                                            </Box>
                                            <Box
                                                sx={{
                                                    flex: 0.125,
                                                    px: 1,
                                                }}
                                            >
                                                Short Hin
                                            </Box>
                                            <Box
                                                sx={{
                                                    flex: 0.225,
                                                    px: 1,
                                                }}
                                            >
                                                ICCID
                                            </Box>
                                            <Box
                                                sx={{
                                                    flex: 0.125,
                                                    px: 1,
                                                }}
                                            >
                                                Provider
                                            </Box>
                                            <Box
                                                sx={{
                                                    flex: 0.225,
                                                    px: 1,
                                                }}
                                            >
                                                Part Number
                                            </Box>
                                            <Box
                                                sx={{
                                                    flex: 0.225,
                                                    px: 1,
                                                }}
                                            >
                                                Manufacturer
                                            </Box>
                                        </Box>

                                        {preview?.map((item, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    p: 1,
                                                    borderBottom: 1,
                                                    borderColor: 'divider',
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex: 0.125,
                                                        px: 1,
                                                        fontFamily: 'monospace',
                                                    }}
                                                >
                                                    {item?.deviceSerial}
                                                </Box>

                                                <Box
                                                    sx={{
                                                        flex: 0.125,
                                                        px: 1,
                                                        maxLines: 2,
                                                    }}
                                                >
                                                    {item?.fullHin}
                                                </Box>

                                                <Box
                                                    sx={{
                                                        flex: 0.125,
                                                        px: 1,
                                                        fontFamily: 'monospace',
                                                    }}
                                                >
                                                    {item?.shortHin}
                                                </Box>

                                                <Box
                                                    sx={{
                                                        flex: 0.225,
                                                        px: 1,
                                                        maxLines: 2,
                                                    }}
                                                >
                                                    {item?.iccid}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        flex: 0.125,
                                                        px: 1,
                                                    }}
                                                >
                                                    {item?.provider}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        flex: 0.225,
                                                        px: 1,
                                                    }}
                                                >
                                                    {item?.partNumber} {' - '}
                                                    {'190424'}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        flex: 0.225,
                                                        px: 1,
                                                    }}
                                                >
                                                    {item?.manufacturer}
                                                    {' - '}
                                                    {
                                                        activeManufacturer?.manufacturerName
                                                    }
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </pre>
                            </Paper>
                        </Box>
                    )}
                </Grid>
                {uploading && (
                    <div style={{ marginTop: 20 }}>
                        <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                            {status}
                        </div>

                        <div
                            style={{
                                width: '100%',
                                height: 32,
                                backgroundColor: '#f0f0f0',
                                borderRadius: 16,
                                overflow: 'hidden',
                                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)',
                            }}
                        >
                            <div
                                style={{
                                    width: `${progress}%`,
                                    height: '100%',
                                    background:
                                        progress === 100
                                            ? '#52c41a'
                                            : '#1890ff',
                                    transition: 'width 0.6s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                }}
                            >
                                {progress}%
                            </div>
                        </div>
                    </div>
                )}
            </FormSection>

            <Grid item xs={12} md={12} lg={12}>
                <FormSection>
                    <Button
                        onClick={handleSubmit}
                        disabled={!isFormDirty}
                        variant="contained"
                        fullWidth
                        sx={{ height: 60 }}
                    >
                        Submit Devices for Provisioning
                    </Button>
                </FormSection>
            </Grid>

            {/* <SaveButton
                handleSubmit={handleSubmit}
                handleReset={handleReset}
                disabledSave={!isFormDirty}
                disabledReset={!isFormDirty}
            /> */}
        </Grid>
    )
}

export default MultipleDeviceUploader
